import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  XMarkIcon,
  ChartBarIcon,
  ArrowsRightLeftIcon,
  KeyIcon,
  WalletIcon,
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  XCircleIcon,
  BuildingOfficeIcon,
  GlobeAsiaAustraliaIcon,
  BuildingStorefrontIcon,
  StarIcon,
  UserGroupIcon,
  UserIcon,
  ChevronDownIcon,
  CreditCardIcon
} from '@heroicons/react/24/outline';
import { getUsersData } from '../data/users';
import TransactionModal from './TransactionModal';
import ChangePasswordModal from './ChangePasswordModal';
import useTranslation from '../services/translationService';
import { isUserRestricted } from '../services/breakglassService';
import { amountFractionDigits } from '../utils/currency';

const USER_TYPES = {
  ADMIN: 'admin',
  COUNTRY_ADMIN: 'country',
  CITY_ADMIN: 'city',
  SUPER_AGENT: 'super-agent',
  AGENT: 'agent',
  PLAYER: 'player'
};

// Currency sort order
const CURRENCY_ORDER = ['LBP', 'USD', 'TND', 'TRY', 'IQD', 'KWD'];

const sortCurrencies = (entries) => {
  return [...entries].sort(([currencyA], [currencyB]) => {
    const indexA = CURRENCY_ORDER.indexOf(currencyA);
    const indexB = CURRENCY_ORDER.indexOf(currencyB);
    
    // If both are in the order array, sort by their position
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }
    // If only A is in the array, A comes first
    if (indexA !== -1) return -1;
    // If only B is in the array, B comes first
    if (indexB !== -1) return 1;
    // If neither is in the array, sort alphabetically
    return currencyA.localeCompare(currencyB);
  });
};

// User type hierarchy levels (higher number = higher authority)
const userTypeHierarchy = {
  'player': 1,
  'agent': 2,
  'super-agent': 3,
  'city': 4,
  'country': 5,
  'admin': 6
};

const getTypeIcon = (type) => {
  switch (type) {
    case USER_TYPES.ADMIN:
      return BuildingOfficeIcon;
    case USER_TYPES.COUNTRY_ADMIN:
      return GlobeAsiaAustraliaIcon;
    case USER_TYPES.CITY_ADMIN:
      return BuildingStorefrontIcon;
    case USER_TYPES.SUPER_AGENT:
      return StarIcon;
    case USER_TYPES.AGENT:
      return UserGroupIcon;
    case USER_TYPES.PLAYER:
    default:
      return UserIcon;
  }
};

const getTypeColor = (type) => {
  switch (type) {
    case USER_TYPES.ADMIN:
      return 'text-purple-500';
    case USER_TYPES.COUNTRY_ADMIN:
      return 'text-blue-500';
    case USER_TYPES.CITY_ADMIN:
      return 'text-green-500';
    case USER_TYPES.SUPER_AGENT:
      return 'text-orange-500';
    case USER_TYPES.AGENT:
      return 'text-yellow-500';
    case USER_TYPES.PLAYER:
    default:
      return 'text-gray-500';
  }
};

const UserDeepViewModal = ({ 
  isOpen, 
  onClose, 
  node, 
  userType, 
  onViewReport, 
  onTransaction,
  parentNode = null,
  onNavigateToUser = null
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [networkBalances, setNetworkBalances] = useState({});
  const [networkBreakdown, setNetworkBreakdown] = useState({});
  const [includeOwnBalance, setIncludeOwnBalance] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isRestricted, setIsRestricted] = useState(false);
  const modalRef = useRef(null);
  const [startY, setStartY] = useState(0);
  const [expandedCurrency, setExpandedCurrency] = useState(null);

  // Detect iOS device
  useEffect(() => {
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    setIsIOS(isIOSDevice);
  }, []);

  // Check if user is restricted
  useEffect(() => {
    const checkRestriction = async () => {
      const restricted = await isUserRestricted();
      setIsRestricted(restricted);
    };
    checkRestriction();
  }, []);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle swipe down to close on mobile
  const handleTouchStart = (e) => {
    if (!modalRef.current) return;
    if (!e.touches || !e.touches[0]) return;
    const touch = e.touches[0];
    setStartY(touch.clientY);
  };

  const handleTouchMove = (e) => {
    if (!modalRef.current) return;
    if (!e.touches || !e.touches[0]) return;
    const touch = e.touches[0];
    const currentY = touch.clientY;
    const diff = currentY - startY;
    const scrollTop = modalRef.current.scrollTop;
    
    // Only allow dragging down when at the top of the scroll
    if (diff > 0 && scrollTop <= 0) {
      e.preventDefault();
      // Apply visual feedback
      const dragDistance = Math.min(diff, 200);
      modalRef.current.style.transform = `translateY(${dragDistance}px)`;
      modalRef.current.style.transition = 'none';
    }
  };

  const handleTouchEnd = (e) => {
    if (!modalRef.current) return;
    if (!e.changedTouches || !e.changedTouches[0]) return;
    
    const touch = e.changedTouches[0];
    const currentY = touch.clientY;
    const diff = currentY - startY;
    
    // Reset transform with animation
    modalRef.current.style.transition = 'transform 0.3s ease-out';
    modalRef.current.style.transform = '';
    
    // Close if dragged down more than 150px
    if (diff > 150) {
      setTimeout(() => {
        onClose();
      }, 100);
    }
  };

  // Optimized: Calculate network balance with breakdown by user
  const calculateNetworkBalanceWithBreakdown = (username, usersData, includeOwn = true) => {
    if (!username || !usersData) return { totals: {}, breakdown: {} };
    
    const user = usersData[username];
    if (!user) return { totals: {}, breakdown: {} };
    
    const totals = {};
    const topPerCurrency = {}; // { currency: [top 3 contributors] }
    
    const processBalances = (userData) => {
      if (!userData?.balances) return;
      
      Object.entries(userData.balances).forEach(([currency, balance]) => {
        const amount = typeof balance === 'object' ? (balance.amount || balance.value || 0) : balance;
        const numAmount = parseFloat(amount) || 0;
        if (numAmount <= 0) return;
        
        totals[currency] = (totals[currency] || 0) + numAmount;
        
        if (!topPerCurrency[currency]) {
          topPerCurrency[currency] = [];
        }
        
        topPerCurrency[currency].push({
          username: userData.username,
          name: userData.name || userData.username,
          type: userData.type,
          balance: numAmount
        });
        
        // Keep only top 3 (optimization: don't let array grow too large)
        if (topPerCurrency[currency].length > 10) {
          topPerCurrency[currency].sort((a, b) => b.balance - a.balance);
          topPerCurrency[currency] = topPerCurrency[currency].slice(0, 3);
        }
      });
    };
    
    if (includeOwn) {
      processBalances(user);
    }
    
    // Build parent->children index ONCE in O(N), then DFS in O(K)
    // This replaces the old O(K*N) approach that filtered all users per stack node
    const childrenByParent = {};
    for (const u of Object.values(usersData)) {
      if (u?.parentUsername) {
        if (!childrenByParent[u.parentUsername]) {
          childrenByParent[u.parentUsername] = [];
        }
        childrenByParent[u.parentUsername].push(u);
      }
    }
    
    // DFS using the pre-built index
    const stack = [username];
    const visited = new Set();
    
    while (stack.length > 0) {
      const currentUsername = stack.pop();
      if (visited.has(currentUsername)) continue;
      visited.add(currentUsername);
      
      const children = childrenByParent[currentUsername] || [];
      for (const child of children) {
        if (child?.username) {
          processBalances(child);
          stack.push(child.username);
        }
      }
    }
    
    // Final sort to get top 3 per currency
    const breakdown = {};
    Object.keys(topPerCurrency).forEach(currency => {
      breakdown[currency] = topPerCurrency[currency]
        .sort((a, b) => b.balance - a.balance)
        .slice(0, 3);
    });
    
    return { totals, breakdown };
  };

  // Calculate network balance in background (deferred to avoid blocking modal open)
  useEffect(() => {
    if (!isOpen || !node || node.type === 'player') {
      setNetworkBalances({});
      setNetworkBreakdown({});
      return;
    }

    // Start with empty while calculating
    setNetworkBalances({});
    setNetworkBreakdown({});
    
    let cancelled = false;
    
    // Use setTimeout(0) to yield to the browser so the modal renders first
    const timerId = setTimeout(async () => {
      try {
        const { success, data } = await getUsersData();
        if (cancelled) return;
        if (success && data.users[node.username]) {
          const { totals, breakdown } = calculateNetworkBalanceWithBreakdown(node.username, data.users, includeOwnBalance);
          if (cancelled) return;
          setNetworkBalances(totals);
          setNetworkBreakdown(breakdown);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Error calculating network balance:', error);
        }
      }
    }, 0);

    return () => {
      cancelled = true;
      clearTimeout(timerId);
    };
  }, [isOpen, node?.username, includeOwnBalance]);

  // Check if transaction is allowed
  const isTransactionAllowed = () => {
    if (!node?.type) return false;
    
    if (userType === 'admin' || userType === 'country') {
      return true;
    }

    switch (userType) {
      case 'city':
        return node.type === 'super-agent';
      case 'super-agent':
        return node.type === 'agent' || node.type === 'player';
      case 'agent':
        return node.type === 'player';
      default:
        return false;
    }
  };

  const handleViewFullDetails = () => {
    if (!node?.username) return;
    navigate(`/users/${node.username}/deep-view`);
    onClose();
  };

  const handleViewReport = () => {
    if (!node?.username) return;
    try {
      navigate('/reports', { 
        state: { 
          selectedUser: {
            ...node,
            attributes: node.attributes || {},
            balances: node.balances || {}
          }
        }
      });
      onClose();
    } catch (error) {
      console.error('Error navigating to reports:', error);
    }
  };

  const handleTransactionClick = () => {
    if (!node?.username) return;
    setShowTransactionModal(true);
  };

  const handleChangePasswordClick = () => {
    if (!node?.username) return;
    setShowChangePasswordModal(true);
  };

  const handleNavigateToBetHistory = () => {
    if (!node?.username) return;
    navigate('/bet-history', { 
      state: { 
        selectedUser: {
          username: node.username,
          name: node.name,
          type: node.type,
          attributes: node.attributes || {},
          balances: node.balances || {}
        }
      }
    });
    onClose();
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('common.na', 'N/A');
    return new Date(dateString).toLocaleDateString();
  };

  const formatBalance = (amount = 0) => {
    const hasDecimals = amount % 1 !== 0;
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: hasDecimals ? 2 : 0,
      maximumFractionDigits: hasDecimals ? 2 : 0,
    }).format(amount);
  };

  // Early return for closed modal or invalid data
  if (!isOpen) return null;
  if (!node || !node.username) return null;

  const TypeIcon = getTypeIcon(node.type);
  

  return (
    <>
      {/* Modal Overlay */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center"
        onClick={onClose}
      >
        {/* Modal Content */}
        <div 
          ref={modalRef}
          className={`bg-white dark:bg-gray-800 w-full sm:max-w-lg max-h-[85vh] sm:max-h-[90vh] overflow-y-scroll shadow-xl transition-transform
            ${isIOS ? 'rounded-t-3xl sm:rounded-2xl pb-safe' : 'rounded-t-2xl sm:rounded-lg'}`}
          onClick={(e) => e.stopPropagation()}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {/* Swipe Indicator (Mobile) */}
          <div className="sm:hidden flex justify-center pt-2 pb-1">
            <div className="w-12 h-1 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
          </div>

          {/* Header */}
          <div className={`sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700`}>
            <div className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <TypeIcon className={`w-6 h-6 flex-shrink-0 ${getTypeColor(node?.type || 'player')}`} />
                <div className="min-w-0">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white truncate">
                    {node?.name || node?.username || 'Unknown'}
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    @{node?.username || 'unknown'} • {t(`userTypes.${node?.type || 'player'}`, node?.type || 'player')}
                  </p>
                </div>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                }}
                className={`p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex-shrink-0
                  ${isIOS ? 'active:scale-95' : ''}`}
                aria-label="Close"
              >
                <XMarkIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            {/* Full Details Button */}
            <div className="px-4 pb-3">
              <button
                onClick={handleViewFullDetails}
                className={`w-full flex items-center justify-center gap-2 p-2.5 rounded-lg transition-all
                  bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600
                  text-gray-700 dark:text-gray-200 font-medium border border-gray-300 dark:border-gray-600
                  ${isIOS ? 'active:scale-95 rounded-2xl' : ''}`}
              >
                <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                <span className="text-sm">{t('userDeepView.viewFullDetails', 'View Full Details')}</span>
              </button>
            </div>

            {/* Quick Actions */}
            <div className="px-4 pb-4">
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={handleViewReport}
                  className={`flex flex-col items-center justify-center gap-1 p-3 rounded-lg transition-all
                    bg-purple-50 dark:bg-purple-900/10 border border-purple-200 dark:border-purple-800
                    hover:bg-purple-100 dark:hover:bg-purple-900/20
                    ${isIOS ? 'active:scale-95' : ''}`}
                >
                  <ChartBarIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  <span className="text-xs font-medium text-gray-900 dark:text-white">
                    {t('agentTree.actions.viewReport', 'View Report')}
                  </span>
                </button>

                {isTransactionAllowed() && !isRestricted && (
                  <button
                    onClick={handleTransactionClick}
                    className={`flex flex-col items-center justify-center gap-1 p-3 rounded-lg transition-all
                      bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800
                      hover:bg-green-100 dark:hover:bg-green-900/20
                      ${isIOS ? 'active:scale-95' : ''}`}
                  >
                    <ArrowsRightLeftIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
                    <span className="text-xs font-medium text-gray-900 dark:text-white">
                      {t('agentTree.actions.transaction', 'Transaction')}
                    </span>
                  </button>
                )}

                {(node?.type === 'super-agent' || node?.type === 'agent' || node?.type === 'player') && (
                  <button
                    onClick={handleNavigateToBetHistory}
                    className={`flex flex-col items-center justify-center gap-1 p-3 rounded-lg transition-all
                      bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800
                      hover:bg-amber-100 dark:hover:bg-amber-900/20
                      ${isIOS ? 'active:scale-95' : ''}`}
                  >
                    <CreditCardIcon className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                    <span className="text-xs font-medium text-gray-900 dark:text-white">
                      {t('userDeepView.betHistory', 'Bet History')}
                    </span>
                  </button>
                )}

                {!isRestricted && (
                  <button
                    onClick={handleChangePasswordClick}
                    className={`flex flex-col items-center justify-center gap-1 p-3 rounded-lg transition-all
                      bg-indigo-50 dark:bg-indigo-900/10 border border-indigo-200 dark:border-indigo-800
                      hover:bg-indigo-100 dark:hover:bg-indigo-900/20
                      ${isIOS ? 'active:scale-95' : ''}`}
                  >
                    <KeyIcon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                    <span className="text-xs font-medium text-gray-900 dark:text-white">
                      {t('agentTree.actions.changePassword', 'Change Password')}
                    </span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 space-y-3">
            {/* Basic Info */}
            <section className={`bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3 ${isIOS ? 'rounded-2xl' : ''}`}>
              <div className="space-y-2">
                {parentNode && parentNode.username && (
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-gray-600 dark:text-gray-400">{t('agentTree.parent', 'Parent')}</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onNavigateToUser && parentNode) {
                          onNavigateToUser(parentNode);
                        } else if (parentNode.username) {
                          navigate(`/users/${parentNode.username}/deep-view`);
                          onClose();
                        }
                      }}
                      className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                    >
                      {React.createElement(getTypeIcon(parentNode.type || 'player'), {
                        className: `w-3 h-3 ${getTypeColor(parentNode.type || 'player')}`
                      })}
                      @{parentNode.username}
                    </button>
                  </div>
                )}
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-gray-600 dark:text-gray-400">{t('users.status', 'Status')}</span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    node.attributes?.blocked 
                      ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' 
                      : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                  }`}>
                    {node.attributes?.blocked ? (
                      <>
                        <XCircleIcon className="w-3 h-3 mr-1" />
                        {t('userDeepView.blocked', 'Blocked')}
                      </>
                    ) : (
                      <>
                        <CheckCircleIcon className="w-3 h-3 mr-1" />
                        {t('userDeepView.active', 'Active')}
                      </>
                    )}
                  </span>
                </div>
              </div>
            </section>

            {/* Wallet Balances */}
            <section className={`bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3 ${isIOS ? 'rounded-2xl' : ''}`}>
              <h3 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white flex items-center gap-2">
                <WalletIcon className="w-4 h-4 text-indigo-500" />
                {t('userDeepView.walletBalances', 'Wallet Balances')}
              </h3>
              
              {/* Personal Balance */}
              <div className="mb-3 bg-green-50 dark:bg-green-900/10 rounded-lg p-2 border border-green-200 dark:border-green-800">
                <p className="text-xs font-semibold text-green-800 dark:text-green-300 mb-2 flex items-center gap-1">
                  <UserIcon className="w-3 h-3" />
                  {t('userDeepView.personalBalance', 'Personal Balance')}
                </p>
                {node?.balances && Object.keys(node.balances).length > 0 ? (
                  <div className="grid grid-cols-2 gap-2">
                  {sortCurrencies(Object.entries(node.balances)).map(([currency, balance]) => {
                    const amount = typeof balance === 'object' ? (balance.amount || balance.value || 0) : balance;
                    const numericAmount = parseFloat(amount) || 0;
                    const digits = amountFractionDigits(currency, numericAmount);
                    
                    return (
                    <div
                      key={currency}
                      className="rounded-lg border p-2 transition-all bg-white dark:bg-gray-800 border-green-300 dark:border-green-700"
                    >
                      <div className="flex flex-col">
                        <span className="text-xs text-green-700 dark:text-green-400 font-semibold">
                          {currency}
                        </span>
                        <span className="text-base font-bold mt-0.5 text-green-800 dark:text-green-300">
                          {new Intl.NumberFormat('en-US', {
                            minimumFractionDigits: digits,
                            maximumFractionDigits: digits,
                          }).format(numericAmount)}
                        </span>
                      </div>
                      </div>
                    );
                  })}
                </div>
                ) : (
                  <div className="text-center py-3 bg-white dark:bg-gray-800 rounded-lg border border-green-200 dark:border-green-700">
                    <WalletIcon className="w-5 h-5 text-green-400 dark:text-green-600 mx-auto mb-1" />
                    <p className="text-xs text-green-600 dark:text-green-400">
                      {t('userDeepView.noWalletData', 'No wallet data available')}
                    </p>
                  </div>
                )}
              </div>

              {/* Network Balance - Hide for Players */}
              {node?.type !== 'player' && networkBreakdown && Object.keys(networkBreakdown).length > 0 && (
                <div className="bg-blue-50 dark:bg-blue-900/10 rounded-lg p-2 border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-blue-800 dark:text-blue-300 flex items-center gap-1">
                      <UserGroupIcon className="w-3 h-3" />
                      {t('userDeepView.networkBalance', 'Network Balance')}
                    </p>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={includeOwnBalance}
                        onChange={(e) => setIncludeOwnBalance(e.target.checked)}
                        className="w-3 h-3 text-blue-600 bg-white border-blue-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2"
                      />
                      <span className="text-[10px] text-blue-700 dark:text-blue-400 font-medium">
                        {t('userDeepView.includeOwn', 'Include own')}
                      </span>
                    </label>
                  </div>
                  <p className="text-[10px] text-blue-600 dark:text-blue-400 mb-2 ml-4">
                    {t('userDeepView.includesDownline', 'Includes all subordinates')}
                  </p>
                  {(() => {
                    if (!networkBalances || Object.keys(networkBalances).length === 0) {
                      return (
                        <div className="text-center py-2">
                          <div className="animate-pulse">
                            <div className="h-2 bg-blue-200 dark:bg-blue-800 rounded w-1/3 mx-auto"></div>
                          </div>
                        </div>
                      );
                    }
                    
                    const filteredEntries = sortCurrencies(Object.entries(networkBalances))
                      .filter(([currency, balance]) => {
                        const amount = typeof balance === 'object' ? (balance.amount || balance.value || 0) : balance;
                        const numericAmount = parseFloat(amount) || 0;
                        return numericAmount > 0; // Only show currencies with positive balance
                      });
                    
                    if (filteredEntries.length === 0) {
                      return (
                        <p className="text-xs text-blue-600 dark:text-blue-400 text-center py-2">
                          {t('userDeepView.noNetworkBalance', 'No network balance')}
                        </p>
                      );
                    }
                    
                    return (
                      <div className="space-y-2">
                        {filteredEntries.map(([currency, balance]) => {
                          const amount = typeof balance === 'object' ? (balance.amount || balance.value || 0) : balance;
                          const numericAmount = parseFloat(amount) || 0;
                          const digits = amountFractionDigits(currency, numericAmount);
                          const topContributors = networkBreakdown[currency] || [];
                          const isExpanded = expandedCurrency === currency;
                          
                          return (
                            <div key={currency} className="space-y-1">
                              <button
                                onClick={() => setExpandedCurrency(isExpanded ? null : currency)}
                                className="w-full rounded-lg border p-2 transition-all bg-white dark:bg-gray-800 border-blue-300 dark:border-blue-700 hover:border-blue-400 dark:hover:border-blue-600"
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex flex-col items-start">
                                    <span className="text-xs text-blue-700 dark:text-blue-400 font-semibold">
                                      {currency}
                                    </span>
                                    <span className="text-base font-bold text-blue-800 dark:text-blue-300">
                                      {new Intl.NumberFormat('en-US', {
                                        minimumFractionDigits: digits,
                                        maximumFractionDigits: digits,
                                      }).format(numericAmount)}
                                    </span>
                                  </div>
                                  {topContributors.length > 0 && (
                                    <div className="flex items-center gap-1">
                                      <span className="text-[10px] text-blue-600 dark:text-blue-400">
                                        {t('userDeepView.topContributors', 'Top 3')}
                                      </span>
                                      <ChevronDownIcon className={`w-3 h-3 text-blue-600 dark:text-blue-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                    </div>
                                  )}
                                </div>
                              </button>
                              
                              {isExpanded && topContributors.length > 0 && (
                                <div className="pl-2 space-y-1">
                                  {topContributors.map((contributor, idx) => {
                                    const ContribIcon = getTypeIcon(contributor.type);
                                    const contribDigits = amountFractionDigits(currency, contributor.balance);
                                    return (
                                      <div
                                        key={contributor.username}
                                        className="flex items-center justify-between py-1.5 px-2 rounded bg-blue-50/50 dark:bg-blue-900/5 border-l-2 border-blue-400 dark:border-blue-500"
                                      >
                                        <div className="flex items-center gap-2 min-w-0 flex-1">
                                          <span className="text-[10px] font-bold text-blue-600 dark:text-blue-400 w-4">
                                            #{idx + 1}
                                          </span>
                                          <ContribIcon className={`w-3 h-3 flex-shrink-0 ${getTypeColor(contributor.type)}`} />
                                          <span className="text-xs text-gray-700 dark:text-gray-300 truncate">
                                            {contributor.name}
                                          </span>
                                        </div>
                                        <span className="text-xs font-bold text-blue-700 dark:text-blue-300 ml-2">
                                          {new Intl.NumberFormat('en-US', {
                                            minimumFractionDigits: contribDigits,
                                            maximumFractionDigits: contribDigits,
                                          }).format(contributor.balance)}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              )}
            </section>
          </div>

          {/* Bottom padding for tab bar on mobile */}
          <div className="h-20 sm:h-4"></div>
        </div>
      </div>

      {/* Transaction Modal */}
      {showTransactionModal && (
        <TransactionModal
          isOpen={showTransactionModal}
          onClose={() => setShowTransactionModal(false)}
          preselectedUser={node}
        />
      )}

      {/* Change Password Modal */}
      {showChangePasswordModal && (
        <ChangePasswordModal
          isOpen={showChangePasswordModal}
          onClose={() => setShowChangePasswordModal(false)}
          username={node.username}
          userType={node.type}
        />
      )}
    </>
  );
};

export default UserDeepViewModal;
